<template>
  <no-ssr>
    <popup
      name="address-map-popup"
      ref="addressMapPopup"
      class="address-map-popup"
      @close="$emit('toggle', true)"
    >
      <template>
        <div class="address-map-popup__overlay" slot="overlay" @click="$emit('toggle', true)" />
        <p slot="header" class="address-map-popup__title">
          <span>{{ !selectedAddress ? $t('Enter your location') : $t('Is this where you are?') }}</span><br>
          <span class="address-map-popup__subtitle">{{ !selectedAddress ? $t('Check availability and delivery fees.') : $t('Confirming your location helps us determine availability and delivery fees.') }}</span>
        </p>
        <div slot="content" class="address-map-popup__content">
          <base-address-autocomplete-input
            @address-selected="setSelectedAddress"
            @handleOpenOverlayMapModal="$emit('openMapModal')"
            :show-map-modal-overlay="true"
            :clear-input="clearInput"
          />
          <div class="geolocation-control">
            <mini-button-full class="address_btn" :disabled="!locateAvailable" @click.native="locate">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
                <path fill="#fff" fill-rule="evenodd" clip-rule="evenodd" d="M15.333 7.333h-1.082a.333.333 0 0 1-.328-.28 6.011 6.011 0 0 0-4.976-4.976.333.333 0 0 1-.28-.328V.667a.667.667 0 0 0-1.334 0v1.082c0 .163-.119.302-.28.328a6.011 6.011 0 0 0-4.976 4.976.333.333 0 0 1-.328.28H.667a.667.667 0 0 0 0 1.334h1.082c.163 0 .302.119.328.28a6.011 6.011 0 0 0 4.976 4.976c.161.025.28.165.28.328v1.082a.667.667 0 1 0 1.334 0v-1.082c0-.163.119-.303.28-.328a6.012 6.012 0 0 0 4.976-4.976.333.333 0 0 1 .328-.28h1.082a.667.667 0 1 0 0-1.334ZM8 12.667a4.667 4.667 0 1 1 0-9.334 4.667 4.667 0 0 1 0 9.334Zm0-2a2.667 2.667 0 1 0 0-5.334 2.667 2.667 0 0 0 0 5.334Z" />
              </svg>
              {{ locatingError ? $t('Refresh') : $t('Share location') }}
            </mini-button-full>
            <span v-if="locatingError">
              {{ $t('Error happened during address locating') }}
            </span>
          </div>
          <div class="address-map-popup__actions">
            <mini-button-full v-if="!selectedAddress && !currentUser" class="login_btn" @click.native="onOpenLoginModal">
              {{ $t('Log in for saved addresses') }}
            </mini-button-full>
            <mini-button-full v-else class="confirm_btn" @click.native="onConfirmAddress" :disabled="!selectedAddress">
              {{ $t('Confirm') }}
            </mini-button-full>
          </div>
        </div>
      </template>
    </popup>
  </no-ssr>
</template>

<script async>
import { mapActions, mapState } from 'vuex';
import Popup from 'theme/components/core/Popup.vue';
import NoSSR from 'vue-no-ssr';
import config from 'config';

const MiniButtonFull = () => import(/* webpackChunkName: "vsf-mini-button-full" */ 'theme/components/theme/MiniButtonFull.vue');

export default {
  name: 'AddressMapPopup',
  components: {
    Popup,
    MiniButtonFull,
    BaseAddressAutocompleteInput: () => import('theme/components/core/blocks/Form/BaseAddressAutocompleteInput.vue'),
    'no-ssr': NoSSR
  },
  props: {
    open: {
      type: Boolean,
      required: true,
      default: false
    },
    clearInput: {
      type: Boolean,
      required: true,
      default: false
    }
  },
  data () {
    return {
      locating: false,
      locateAvailable: false,
      locationInaccurate: false,
      locatingError: null,
      timeout: null
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.user.current,
      selectedAddress: (state) => state.address.selectedAddress
    }),
    hasHouseNumber () {
      return !!(this.selectedAddress && this.selectedAddress.address.house_number);
    }
  },
  methods: {
    ...mapActions({
      setSelectedAddress: 'address/setSelectedAddress'
    }),
    onLocationFound ({ coords }) {
      clearTimeout(this.timeout);
      this.locatingError = null;
      this.timeout = setTimeout(() => {
        fetch(`https://${config.rad.base_url}/address/suggestion?address=${coords.latitude}, ${coords.longitude}&snh=0`)
          .then(response => response.json())
          .then(data => {
            data = data.length ? data[0] : null;
            if (!data) {
              return;
            }
            this.setSelectedAddress(data);
            this.$bus.$emit('address-autocomplete-set-search', data.display_name);
          })
          .catch(error => {
            console.error('Error fetching data from Addresses service: ', error)
          })
      }, 500);
      if (coords.accuracy > 50) {
        this.locationInaccurate = true;
        console.log('Accuracy is too low: ', coords.accuracy);
      }
      this.locating = false;
    },
    onLocationError (e) {
      console.error(e);
      this.locatingError = e;
      this.locating = false;
    },
    locate () {
      this.locationInaccurate = false;

      this.locating = true;
      navigator.geolocation.getCurrentPosition(this.onLocationFound, this.onLocationError, {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 10000
      });
    },
    onOpenLoginModal () {
      this.$emit('toggle', true);
      this.$store.commit('ui/setAuthElem', 'login');
      this.$bus.$emit('modal-show', 'modal-signup');
    },
    onConfirmAddress () {
      if (this.hasHouseNumber) {
        this.$emit('selectAddress', this.selectedAddress);
      }
      this.$emit('openAddressConfirmationModal');
    }
  },
  mounted () {
    this.locateAvailable = !!navigator.geolocation;
  },
  watch: {
    open () {
      this.$bus.$emit('popup-toggle', 'address-map-popup', this.open);
    }
  }
}
</script>

<style scoped lang="scss">
@import '~theme/css/layout/screen';

.address-map-popup {
  cursor: auto;
  width: max-content;

  &__overlay {
    width: 200vw;
    height: 100vh;
    position: fixed;
    top: -49px;
    left: -100%;
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
  }

  &__subtitle {
    display: block;
    margin-top: 8px;
    font-size: 16px;
    font-weight: 500;
  }

  &__content {
    display: flex;
    flex-direction: column;
    row-gap: 12px;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    column-gap: 12px;
  }

  .geolocation-control {
    display: flex;
    flex-direction: column;
    text-align: start;

    svg {
      width: 24px;
      height: 24px;
    }
  }

  @media only screen and (max-width: $screen-sm-l) {
    width: calc(100vw - 24px);
    left: 4px;
  }
}
</style>

<style lang="scss">
@import '~theme/css/layout/screen';

.address-map-popup {
  &__content {
    .address-autocomplete__input-field {
      margin: 0 !important;
      padding: 0;
    }

    .addresses-list {
      margin: 0 !important;
    }
  }

  &__actions {
    .button-full {
      padding: 10px !important;
      min-width: unset !important;
    }
  }

  .geolocation-control {
    .button-full {
      padding: 10px !important;
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 12px;
    }
  }

  @media only screen and (max-width: $screen-sm-l) {
    &::before {
      left: 4px;
    }
  }
}
</style>
